import authService from '@platform/core/src/services/authService';
import config from '@platform/core/src/config';
const API_BASE_URL = config.API_BASE_URL;
const FITNESS_PROFILE_API_PREFIX = '/api/v1/profiles/fitness';
/**
 * Fetches the fitness profile for the current user.
 * @returns {Promise<Object>} FitnessProfileResponse schema object.
 * @throws {Error} If fetching fails.
 */
export const getMyFitnessProfile = async () => {
  const url = `${API_BASE_URL}${FITNESS_PROFILE_API_PREFIX}/me`;
  console.log(`[workoutsmart/profileApi] Fetching fitness profile from: ${url}`);
  try {
    const response = await authService.authFetch(url);
    if (response.status === 404) {
      console.log(`[workoutsmart/profileApi] Fitness profile not found (404). Returning default.`);
      return { fundamental_metrics: {}, profile_data: {}, goals: [] };
    }
    if (!response.ok) {
      let errorDetail = `Failed to fetch fitness profile: ${response.status}`;
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorDetail;
      } catch (e) {  }
      throw new Error(errorDetail);
    }
    const profile = await response.json();
    console.log(`[workoutsmart/profileApi] Fetched fitness profile.`);
    return profile;
  } catch (error) {
    console.error('[workoutsmart/profileApi] Error fetching fitness profile:', error);
    if (!error.message?.includes('404')) {
      authService.trackNetworkError('getMyFitnessProfile', error);
      throw error;
    }
    return { fundamental_metrics: {}, profile_data: {}, goals: [] };
  }
};
/**
 * Cleans the profile data payload to remove empty/null optional fields
 * before sending it to the backend, preventing 422 errors.
 * Updated: Sets value to null if key exists but value is empty/invalid,
 * allowing removal of fields.
 * @param {object} profileData - The profile data from the component's state.
 * @returns {object} A cleaned payload ready for the API.
 */
const cleanProfilePayload = (profileData) => {
    const payload = {};
    if (profileData.fundamental_metrics) {
        const metrics = {};
        const fm = profileData.fundamental_metrics;
        if (typeof fm.age === 'number' && !isNaN(fm.age) && fm.age > 0) {
            metrics.age = fm.age;
        } else if (Object.prototype.hasOwnProperty.call(fm, 'age')) {
            metrics.age = null;
        }
        if (typeof fm.sex === 'string' && fm.sex) {
            metrics.sex = fm.sex;
        } else if (Object.prototype.hasOwnProperty.call(fm, 'sex')) {
            metrics.sex = null;
        }
        if (typeof fm.current_fitness_level === 'string' && fm.current_fitness_level) {
            metrics.current_fitness_level = fm.current_fitness_level;
        } else if (Object.prototype.hasOwnProperty.call(fm, 'current_fitness_level')) {
            metrics.current_fitness_level = null;
        }
        if (fm.height && typeof fm.height.value === 'number' && !isNaN(fm.height.value) && fm.height.unit) {
            metrics.height = { value: fm.height.value, unit: fm.height.unit };
        } else if (Object.prototype.hasOwnProperty.call(fm, 'height')) {
            metrics.height = null;
        }
        if (fm.weight && typeof fm.weight.value === 'number' && !isNaN(fm.weight.value) && fm.weight.unit) {
            metrics.weight = { value: fm.weight.value, unit: fm.weight.unit };
        } else if (Object.prototype.hasOwnProperty.call(fm, 'weight')) {
            metrics.weight = null;
        }
        if (Object.keys(metrics).length > 0) {
            payload.fundamental_metrics = metrics;
        }
    }
    if (profileData.profile_data) {
        payload.profile_data = profileData.profile_data;
    }
    if (profileData.hasOwnProperty('goals') && Array.isArray(profileData.goals)) {
        payload.goals = profileData.goals;
    }
    if (profileData.hasOwnProperty('physical_considerations')) {
        payload.physical_considerations = profileData.physical_considerations || null;
    }
    return payload;
};
/**
 * Updates the fitness profile for the current user.
 * @param {Object} profileData - Data matching FitnessProfileUpdateRequest schema.
 * @returns {Promise<Object>} The updated FitnessProfileResponse schema object.
 * @throws {Error} If update fails.
 */
export const updateMyFitnessProfile = async (profileData) => {
  const url = `${API_BASE_URL}${FITNESS_PROFILE_API_PREFIX}/me`;
  const cleanedData = cleanProfilePayload(profileData);
  console.log(`[workoutsmart/profileApi] Updating fitness profile (PUT) at: ${url} with cleaned payload:`, cleanedData);
  try {
    const response = await authService.authFetch(url, {
      method: 'PUT',
      body: cleanedData,
    });
    if (!response.ok) {
      let errorDetail = `Failed to update fitness profile: ${response.status}`;
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorDetail;
      } catch (e) {  }
      throw new Error(errorDetail);
    }
    const updatedProfile = await response.json();
    console.log(`[workoutsmart/profileApi] Updated fitness profile (PUT).`);
    return updatedProfile;
  } catch (error) {
    console.error('[workoutsmart/profileApi] Error updating fitness profile (PUT):', error);
    authService.trackNetworkError('updateMyFitnessProfile', error);
    throw error;
  }
};

/**
 * Updates the fitness profile for the current user via PATCH (partial update).
 * @param {Object} profileData - Data matching FitnessProfileUpdateRequest schema.
 * @returns {Promise<Object>} The updated FitnessProfileResponse schema object.
 * @throws {Error} If update fails.
 */
export const patchMyFitnessProfile = async (profileData) => {
  const url = `${API_BASE_URL}${FITNESS_PROFILE_API_PREFIX}/me`;
  const cleanedData = cleanProfilePayload(profileData);
  console.log(`[workoutsmart/profileApi] Patching fitness profile (PATCH) at: ${url} with cleaned payload:`, cleanedData);
  try {
    const response = await authService.authFetch(url, {
      method: 'PATCH',
      body: cleanedData,
    });
    if (!response.ok) {
      let errorDetail = `Failed to patch fitness profile: ${response.status}`;
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorDetail;
      } catch (e) {  }
      throw new Error(errorDetail);
    }
    const updatedProfile = await response.json();
    console.log(`[workoutsmart/profileApi] Patched fitness profile (PATCH).`);
    return updatedProfile;
  } catch (error) {
    console.error('[workoutsmart/profileApi] Error patching fitness profile (PATCH):', error);
    authService.trackNetworkError('patchMyFitnessProfile', error);
    throw error;
  }
};